import React, { lazy, Suspense } from "react";
import LeaveManagementdesktop from "@/assets/leaveManagement/waggex-leave-management-control-transparency-india.webp?w=600&format=webp";
import LeaveManagementmobile from "@/assets/leaveManagement/waggex-leave-management-control-transparency-india-mobile.webp?w=350&format=webp";

import {
  FaCalendarCheck,
  FaNotesMedical,
  FaMoneyBillWave,
} from "react-icons/fa";
import { MessageCircleMore, TriangleAlert } from "lucide-react";
import { FaUmbrellaBeach } from "react-icons/fa6";
import { Helmet } from "react-helmet-async";
// import DynamicFeaturesSection from "../components/common/DynamicFeaturesSection";
import StatsSection from "../components/common/StatsSection";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";
const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);
const problems = [
  {
    title: "WhatsApp Requests",
    description:
      "Leave requests on chat can get missed, delayed, or hard to track later.",
    icon: <MessageCircleMore size={42} strokeWidth={1.8} />,
  },
  {
    title: "Payroll Errors",
    description:
      "Unpaid leaves and absences can affect salary calculation if records are incorrect.",
    hideIcon: true,
  },
  {
    title: "Approval Confusion",
    description:
      "Managers and HR may not have clear visibility of pending and approved leaves.",
    hideIcon: true,
  },
  {
    title: "Wrong Leave Balance",
    description:
      "Manual balance calculation can create confusion between HR and employees.",
    icon: <TriangleAlert size={42} strokeWidth={1.8} />,
  },
];
const leaves = [
  {
    title: "Earned Leave",
    desc: "Manage earned leave balances automatically based on company policy, employee eligibility, and yearly leave rules.",
    items: [
      "Auto leave balance updates",
      "Employee-wise leave tracking",
      "Policy-based leave control",
    ],
    icon: FaCalendarCheck,
  },
  {
    title: "Casual Leave",
    desc: "Simplify casual leave requests with faster approvals, balance tracking, and organized leave records.",
    items: [
      "Quick leave applications",
      "Manager approval visibility",
      "Real-time balance tracking",
    ],
    icon: FaUmbrellaBeach,
  },
  {
    title: "Medical Leave",
    desc: "Track medical leave requests, approvals, and employee leave history from one centralized system.",
    items: [
      "Medical leave records",
      "HR/Admin approval flow",
      "Employee leave history",
    ],
    icon: FaNotesMedical,
  },
  {
    title: "Unpaid Leave",
    desc: "Manage unpaid leave records clearly to support salary deductions and payroll accuracy.",
    items: [
      "Payroll deduction visibility",
      "Organized unpaid records",
      "Salary calculation support",
    ],
    icon: FaMoneyBillWave,
  },
];
const leaveMangFeatures = [
  // ROW 1
  {
    id: 1,
    title: "Online Leave Requests",
    desc: "Employees can apply for leave directly from the Waggex app or web portal with a simple and fast application process.",
  },
  {
    id: 2,
    title: "Approval Workflow",
    desc: "Managers can approve or reject leave requests instantly from web or mobile dashboards with real-time updates.",
  },
  {
    id: 3,
    title: "Leave Balance Tracking",
    desc: "Leave balances update automatically after every approval, cancellation, or adjustment ensuring accurate records.",
  },

  // ROW 2
  {
    id: 4,
    title: "Yearly Leave Policy",
    desc: "Set yearly leave rules based on company policy, employee roles, and different leave categories with full flexibility.",
  },
  {
    id: 5,
    title: "Attendance Integration",
    desc: "Approved leaves automatically sync with attendance records to maintain accurate work history and compliance.",
  },
  {
    id: 6,
    title: "Payroll-Ready Leave Data",
    desc: "Verified leave records are directly used for salary processing and payroll calculations without manual intervention.",
  },

  // ROW 3
  {
    id: 7,
    title: "Leave Status Updates",
    desc: "Employees receive instant notifications when leave requests are approved, rejected, or modified by HR.",
  },
  {
    id: 8,
    title: "Employee Self-Service",
    desc: "Employees can apply for leave, track balances, and view history without needing HR follow-ups.",
  },
  {
    id: 9,
    title: "Admin Control",
    desc: "HR teams can centrally manage leave rules, approvals, balances, and complete leave records with full control.",
  },
];
const headers = [
  "LEAVE ACTIVITY",
  "MANUAL LEAVE TRACKING",
  "WAGGEX LEAVE MANAGEMENT",
];

const rows = [
  {
    feature: "Leave Requests",
    without: "WhatsApp messages or paper forms",
    with: "Digital leave application system",
  },
  {
    feature: "Approval Status",
    without: "Hard to track in real time",
    with: "Instantly visible approval status",
  },
  {
    feature: "Leave Balance",
    without: "Manual Excel updates every period",
    with: "Automatically updated after each action",
  },
  {
    feature: "Leave Policy",
    without: "Rules shared informally across teams",
    with: "Centralised leave policies applied consistently",
  },
  {
    feature: "Attendance Link",
    without: "Tracked in a separate register",
    with: "Leave and attendance automatically synced",
  },
  {
    feature: "Payroll Impact",
    without: "Manual corrections before every salary run",
    with: "Payroll automatically uses verified leave data",
  },
  {
    feature: "Employee Visibility",
    without: "Employees call HR for balance info",
    with: "Employees check leave balance in the app",
  },
  {
    feature: "Audit Trail",
    without: "No record of changes made",
    with: "Full history of every leave action",
  },
];
const leaveMangFaqs1 = [
  {
    title: "Is my data secure with the Waggex Payroll Web App?",
    content:
      "Yes. Waggex uses SSL encryption, multi-factor authentication, and encrypted backups to protect all business data. Employee records, leave data, and attendance information are securely stored with role-based access control.",
  },
  {
    title: "Can I create more than one company on Waggex?",
    content:
      "Yes. Waggex supports multiple company accounts under a single login. Each company runs independently with its own employees, policies, leave rules, and payroll settings. You can switch between companies from the same dashboard.",
  },
  {
    title: "Does Waggex support rotational or custom leave policies?",
    content:
      "Yes. You can configure rotational or custom leave policies including casual, sick, earned, unpaid, and custom leave types. Each can have its own rules for accrual, carry forward, and approvals.",
  },
];

const leaveMangFaqs2 = [
  {
    title: "Can leave records connect with payroll processing?",
    content:
      "Yes. Approved leave data is automatically synced with payroll. Paid leave, unpaid leave, and deductions are directly reflected in salary processing without manual calculations.",
  },
  {
    title: "Is my data secure with the Waggex Payroll Web App?",
    content:
      "Yes. Waggex ensures security with SSL encryption, multi-factor authentication, encrypted backups, and role-based access control across all HR and payroll modules.",
  },
];
const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Leave Management Software",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex Leave Management Software automates employee leave requests, approval workflows, balance tracking, attendance sync, and payroll-ready leave data for Indian businesses.",
    url: "https://www.waggex.com/leave-management",
    featureList: [
      "Online leave request software India",
      "Leave approval workflow automation",
      "Leave balance tracking India",
      "Attendance and leave sync",
      "Payroll-ready leave data",
      "Custom leave policy management India",
      "Multi-location leave management",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "298",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Waggex Leave Management System?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex Leave Management is an HR software module that digitises employee leave requests, approvals, balance tracking, attendance sync, and payroll-ready records for Indian businesses.",
        },
      },
      {
        "@type": "Question",
        name: "Is My Data Secure With The Waggex App?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex uses SSL encryption, multi-factor authentication, encrypted backups, and role-based access controls to protect all user data.",
        },
      },
      {
        "@type": "Question",
        name: "Can I Create More Than One Company On Waggex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Multiple company accounts operate independently under one login, each with its own employees, leave policies, and payroll settings.",
        },
      },
      {
        "@type": "Question",
        name: "Does Waggex support custom leave policies?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. HR teams can configure custom leave types with accrual rules, carry-forward settings, encashment rules, and approval hierarchies per department or location.",
        },
      },
      {
        "@type": "Question",
        name: "Can leave records connect with payroll processing?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Leave records automatically sync with payroll, including paid leave, deductions, and encashments without manual calculation.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides leave management software and complete HRMS solutions for Indian businesses.",
    areaServed: "IN",
  },
];
export default function LeaveManagement() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      {/* <SEO
        title="Leave Management Software | Employee Leave Tracking & HR Workflows | Waggex"
        description="Automate employee leave management with Waggex. Submit requests via mobile app, track balances in real time, approve workflows, and integrate leave data directly with payroll."
        keywords="Leave management software, Employee leave system, HR leave workflow automation, Digital leave portal, Employee leave tracking software, Payroll integrated leave management, Team leave calendar, Automated leave approval"
        canonical="https://www.waggex.com/leave-management"
      /> */}
      <Helmet>
        <title>
          Leave Management Software For Businesses In India | Waggex
        </title>
        <meta
          name="description"
          content="Automate leave requests, approvals, balances, and leave tracking with Waggex leave management software built for Indian businesses and HR teams."
        />
        <meta
          name="keywords"
          content="leave management software,leave management system India,employee leave tracking software,HR leave approval software India,online leave management system,leave balance tracking software India,automated leave approval software,leave management system for companies India,employee leave request software
"
        />
        <link rel="canonical" href="https://www.waggex.com/leave-management" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d3e0eb]">
        <div className="max-w-6xl mx-auto px-4">
          {/* TOP CONTENT */}
          <div className="text-center top-10 relative">
            <h1 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight min-h-12">
              Leave Management Software For Indian Companies
            </h1>

            <p className="mt-4 text-sm sm:text-base lg:text-lg text-[#10324F] max-w-3xl mx-auto">
              Waggex brings every leave request, approval, balance update, and
              payroll record into one connected platform so your team always
              knows exactly where each leave stands
            </p>

            {/* BUTTONS */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8">
              <button
                onClick={() =>
                  (window.location.href = "https://app.waggex.com/signup")
                }
                className="w-full sm:w-auto px-8 py-3 rounded-xl bg-[#10324F] text-white font-semibold"
              >
                Start Free Today
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto px-8 py-3 rounded-xl border border-[#10324F] text-[#10324F] mb-25 sm:mb-0"
              >
                Book Free Demo
              </button>
            </div>
          </div>

          {/* BOTTOM IMAGE SECTION */}
          <div className="md:mt-20 flex justify-center">
            <div className="relative">
              <picture>
                <source
                  media="(max-width: 639px)"
                  srcSet={LeaveManagementmobile}
                />
                <img
                  src={LeaveManagementdesktop}
                  width={1120}
                  height={418}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Waggex leave management centralised dashboard faster approvals accurate leave balances payroll sync and employee self-service for Indian HR teams"
                  className="w-full h-auto object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="w-full bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6  items-center min-h-330 md:min-h-170">
          {/* Left Content */}
          <div className="max-w-xl">
            <h2 className="text-[#10324F] font-roboto font-bold text-xl sm:text-3xl leading-tight">
              Four Leave Management Problems That Cost HR Teams Hours Every Week
            </h2>

            <p className="mt-6 text-[#10324F] text-base sm:text-lg leading-8">
              Manual leave management creates approval delays, leave balance
              confusion, missing records, and payroll mismatches. Waggex leave
              management system software keeps employee leave records organized
              automatically and simplifies leave approvals for HR teams.
            </p>
          </div>

          {/* Right Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {problems.map((item, index) => (
              <div
                key={index}
                style={{
                  boxShadow: `
          rgb(167 191 210 / 35%) -12px -12px 20px inset,
          rgb(170 188 202 / 22%) -14px -14px 24px
        `,
                }}
                className={`
        bg-white
        rounded-2xl
        border border-[#AEAEC014]
        flex flex-col items-center text-center
 ${item.title === "Payroll Errors" ? "sm:top-20 sm:relative" : ""}
        ${
          item.hideIcon
            ? "justify-start px-6 py-8 h-50"
            : "justify-center px-7 py-10 min-h-70"
        }
      `}
              >
                {/* ICON */}
                {!item.hideIcon && (
                  <div className="flex justify-center mb-7 text-[#10324F]">
                    {item.icon}
                  </div>
                )}

                {/* TITLE */}
                <h2
                  className={`
          text-[#10324F] text-xl
          font-bold
          leading-snug

        `}
                >
                  {item.title}
                </h2>

                {/* DESCRIPTION */}
                <p
                  className={`
          mt-5
          text-[#10324F]
          leading-8
          max-w-85

          ${item.hideIcon ? "text-[15px]" : "text-[16px]"}
        `}
                >
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-[#10324F] font-bold text-xl sm:text-3xl  leading-tight">
              Plan, Approve, and Track Employee Leaves From One Place
            </h2>

            <p className="mt-5 text-[#10324F] text-base sm:text-lg leading-7">
              Waggex gives every HR team a single platform where leave
              applications, approval chains, policy rules, balance records, and
              payroll-linked data operate together updated in real time,
              accessible from anywhere.
            </p>
          </div>

          {/* Cards */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl mx-auto">
            {/* Before */}
            <div
              className="bg-white rounded-3xl border border-[#E4ECF3] shadow-sm p-6 sm:p-8"
              style={{
                boxShadow: `
          rgb(167 191 210 / 40%) -15px -15px 20px inset,
          rgb(170 188 202 / 30%) -20px -20px 30px
        `,
              }}
            >
              <h2 className="text-[#10324F] text-2xl font-bold text-center mb-4">
                Before Waggex
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-4">
                <div className="flex items-start gap-3">
                  <span className="text-red-500 text-xl font-bold leading-none">
                    ✕
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Leave approvals on WhatsApp
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-red-500 text-xl font-bold leading-none">
                    ✕
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Manual leave balance updates
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-red-500 text-xl font-bold leading-none">
                    ✕
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Delayed manager approvals
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-red-500 text-xl font-bold leading-none">
                    ✕
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Payroll errors from leave records
                  </p>
                </div>
              </div>
            </div>

            {/* After */}
            <div
              className="bg-white rounded-3xl border border-[#E4ECF3] shadow-sm p-6 sm:p-8"
              style={{
                boxShadow: `
          rgb(167 191 210 / 40%) -15px -15px 20px inset,
          rgb(170 188 202 / 30%) -20px -20px 30px
        `,
              }}
            >
              <h2 className="text-[#10324F] text-2xl font-bold text-center mb-4">
                After Waggex
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8">
                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl font-bold leading-none">
                    ✓
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Centralized digital leave requests
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl font-bold leading-none">
                    ✓
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Real-time leave balance tracking
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl font-bold leading-none">
                    ✓
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Faster approval workflow
                  </p>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-green-600 text-xl font-bold leading-none">
                    ✓
                  </span>
                  <p className="text-[#10324F] text-sm sm:text-base leading-6">
                    Payroll-ready leave records
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <DynamicFeaturesSection
          heading="Switch to Waggex Leave Management"
          description="Waggex Leave Management covers every step of the leave cycle from the moment an employee applies to the point payroll picks up the data. HR teams get full control, full visibility, and zero manual reconciliation"
          dynamicFeatures={leaveMangFeatures}
        />
        <section className="w-full bg-[#D1DFEA33] py-10 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Heading */}
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-3xl font-bold text-[#10324F]">
                Manage Every Leave Type Your Business Needs
              </h2>
              <p className="mt-4 text-[#10324F] max-w-3xl mx-auto">
                Every business follows different leave rules, approval
                structures, and employee policies. Waggex lets you manage every
                leave category with custom balances, approval workflows, and
                leave settings designed around how your company actually
                operates
              </p>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {leaves.map((leave, index) => {
                const Icon = leave.icon;

                return (
                  <div
                    key={index}
                    className="rounded-2xl shadow-md hover:shadow-xl transition duration-300 p-6 border bg-white  border-white"
                    style={{
                      boxShadow: `
          rgb(167 191 210 / 40%) -15px -15px 20px inset,
          rgb(170 188 202 / 30%) -20px -20px 30px
        `,
                    }}
                  >
                    {/* Icon + Title */}
                    <div className="flex items-center gap-3 mb-4">
                      <Icon
                        className="text-[#10324F] text-2xl"
                        aria-hidden="true"
                      />
                      <h2 className="font-roboto text-lg font-semibold text-[#10324F]">
                        {leave.title}
                      </h2>
                    </div>
                    <hr className="text-[#D1DFEA] " />

                    {/* Description */}
                    <p className="font-roboto text-sm text-[#10324F] leading-tight max-w-md h-20 mt-4">
                      {leave.desc}
                    </p>

                    {/* Features */}
                    <ul className="space-y-2">
                      {leave.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-[#10324F]">▶</span>
                          <span className="font-roboto text-[#10324F]">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <ComparisonTable
          headers={headers}
          rows={rows}
          title="Smarter Leave Management Starts With Waggex"
          subtitle="More than a digital upgrade, a shift to a structured leave workflow that connects every record automatically"
          source="payrollfeature"
        />
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={leaveMangFaqs1}
          faq2={leaveMangFaqs2}
          subtitle="Common questions from Indian businesses evaluating Waggex Shift Management Software"
        />
        <PayrollBanner
          heading="Looking for a Faster Payroll Management System?"
          description="Simplify payroll calculations, attendance-linked salaries, and payslip generation from one centralized platform."
          buttonText="Switch to Waggex Payroll"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors. Start free today."
        />
      </Suspense>
    </>
  );
}
